import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FolderTree, Pencil, Plus, Trash2 } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ErrorState, PageHeader } from '@/components/ui/misc';
import { Field, Input, Select } from '@/components/ui/input';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ui/use-confirm';
import { FamiliaEditRow } from './FamiliaEditRow';

export interface Family {
  id: string;
  code: string;
  name: string;
  parent_id: string | null;
  parent_name: string | null;
  product_count: number;
  model_count: number;
}

/**
 * Familias de artículos, con pantalla propia en el menú.
 *
 * Antes vivían en un botón dentro de Artículos: quien buscaba «Familias» en el menú de Almacén no
 * lo encontraba, y no había forma de llegar sin pasar primero por el listado de artículos.
 */
export function FamiliesPage() {
  const { can } = useAuth();
  const puedeEditar = can('products.write');

  const familiesQuery = useQuery({
    queryKey: ['families'],
    queryFn: () => api<{ data: Family[] }>('/families')
  });
  const familias = familiesQuery.data?.data ?? [];

  const { confirm, dialog: dialogConfirmacion } = useConfirm();
  const [draft, setDraft] = useState({ code: '', name: '', parentId: '' });
  const [editando, setEditando] = useState<{ id: string; name: string; parentId: string } | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [borrando, setBorrando] = useState<string | null>(null);

  const refrescar = async () => {
    await familiesQuery.refetch();
  };

  const crear = async () => {
    if (!draft.code.trim() || !draft.name.trim()) {
      toast.error('La familia necesita código y nombre');
      return;
    }
    setGuardando(true);
    try {
      await api('/families', { method: 'POST', body: {
        code: draft.code, name: draft.name, parentId: draft.parentId || null
      } });
      setDraft({ code: '', name: '', parentId: '' });
      await refrescar();
      toast.success('Familia creada');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setGuardando(false);
    }
  };

  const guardarEdicion = async (datos?: { name: string; parentId: string }) => {
    if (!editando) return;
    setGuardando(true);
    try {
      await api(`/families/${editando.id}`, { method: 'PATCH', body: {
        name: datos?.name ?? editando.name, parentId: (datos?.parentId ?? editando.parentId) || null
      } });
      setEditando(null);
      await refrescar();
      toast.success('Familia actualizada');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (family: Family) => {
    if (!(await confirm({
      title: `Borrar la familia «${family.name}»`,
      description: 'Solo es posible si no tiene artículos ni modelos asignados.',
      confirmLabel: 'Borrar familia', destructive: true
    }))) return;
    setBorrando(family.id);
    try {
      await api(`/families/${family.id}`, { method: 'DELETE' });
      await refrescar();
      toast.success('Familia borrada');
    } catch (error) {
      // El servidor dice cuántos artículos y modelos la usan: es la información útil aquí.
      toast.error(errorMessage(error));
    } finally {
      setBorrando(null);
    }
  };

  // Al elegir familia padre no se ofrecen ni la propia familia ni sus descendientes: el árbol no
  // puede cerrarse sobre sí mismo, y ofrecer una opción que va a dar 409 es una trampa.
  const descendientesDe = (id: string): Set<string> => {
    const fuera = new Set<string>([id]);
    let creciendo = true;
    while (creciendo) {
      creciendo = false;
      for (const family of familias) {
        if (family.parent_id && fuera.has(family.parent_id) && !fuera.has(family.id)) {
          fuera.add(family.id);
          creciendo = true;
        }
      }
    }
    return fuera;
  };
  const opcionesPadre = (excluirId?: string) => {
    const vetados = excluirId ? descendientesDe(excluirId) : new Set<string>();
    return familias.filter((family) => !vetados.has(family.id));
  };

  return (
    <>
      {dialogConfirmacion}
      <PageHeader
        title="Familias de artículos"
        description="Agrupan el catálogo para filtrar y para los informes. Una familia puede colgar de otra."
      />
      <div className="space-y-4">
        {familiesQuery.isError ? (
          <ErrorState
            message={errorMessage(familiesQuery.error)}
            onRetry={() => void familiesQuery.refetch()}
          />
        ) : null}
        {/* Con la lista caída no se ofrece el alta: sin poder elegir familia padre, crearía duplicados. */}
        {puedeEditar && !(familiesQuery.isError && !familiesQuery.data) ? (
          <Card className="p-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nueva familia</h3>
            <div className="grid gap-2 sm:grid-cols-4">
              <Field label="Código" required><Input value={draft.code} onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))} placeholder="ESPEJOS" maxLength={20} /></Field>
              <Field label="Nombre" required><Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="Espejos y cristales" /></Field>
              <Field label="Depende de">
                <Select value={draft.parentId} onChange={(e) => setDraft((d) => ({ ...d, parentId: e.target.value }))}>
                  <option value="">Sin familia padre</option>
                  {opcionesPadre().map((family) => <option key={family.id} value={family.id}>{family.name}</option>)}
                </Select>
              </Field>
              <div className="flex items-end">
                <Button loading={guardando} onClick={() => void crear()}><Plus /> Añadir</Button>
              </div>
            </div>
          </Card>
        ) : null}

        <div className="space-y-2">
          {/* Solo cuando la consulta ha ido bien: con error, «no hay familias» sería mentira. */}
          {familiesQuery.isPending ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">Cargando familias…</p>
          ) : null}
          {familiesQuery.isSuccess && familias.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
              Todavía no hay familias. Crea la primera para poder clasificar los artículos.
            </p>
          ) : null}
          {familias.map((family) => (
            <div key={family.id} className="rounded-lg border border-border bg-card px-3 py-2">
              {editando?.id === family.id ? (
                <FamiliaEditRow
                  familia={editando}
                  opcionesPadre={opcionesPadre(family.id)}
                  guardando={guardando}
                  onGuardar={(datos) => void guardarEdicion(datos)}
                  onCancelar={() => setEditando(null)}
                />
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-xs font-medium">
                      <FolderTree className="size-3.5 text-muted-foreground" />
                      {family.name}
                      <span className="font-mono text-[10px] text-muted-foreground">{family.code}</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {family.parent_name ? `Depende de ${family.parent_name} · ` : ''}
                      {family.product_count === 0 && family.model_count === 0
                        ? 'Sin artículos ni modelos'
                        : [
                          family.product_count > 0 ? `${family.product_count} artículo${family.product_count === 1 ? '' : 's'}` : null,
                          family.model_count > 0 ? `${family.model_count} modelo${family.model_count === 1 ? '' : 's'}` : null
                        ].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {family.product_count + family.model_count > 0 ? (
                      <Badge tone="outline">{family.product_count + family.model_count}</Badge>
                    ) : null}
                    {/* Con la lista caída no se ofrece el alta: sin poder elegir familia padre, crearía duplicados. */}
        {puedeEditar && !(familiesQuery.isError && !familiesQuery.data) ? (
                      <>
                        <Button variant="ghost" size="icon-sm" title="Renombrar"
                          onClick={() => setEditando({ id: family.id, name: family.name, parentId: family.parent_id ?? '' })}>
                          <Pencil />
                        </Button>
                        <Button variant="ghost" size="icon-sm" title="Borrar" loading={borrando === family.id}
                          disabled={borrando !== null} onClick={() => void borrar(family)}>
                          <Trash2 />
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
